import { Injectable } from "@angular/core";
import {
  FileUploaderOptions,
  FileUploader,
  FileItem,
  ParsedResponseHeaders,
  FileLikeObject,
} from "ng2-file-upload";

import { Observable, Subscriber } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { SessionService } from "src/app/store/session/session.service";
import { AmplifyService } from "./amplify.service";

interface FileUploaderActions {
  onCompleteItem(
    item: FileItem,
    response: Object,
    status: number,
    headers: ParsedResponseHeaders
  ): any;
  onCancelItem?(
    item: FileItem,
    response: string,
    status: number,
    headers: ParsedResponseHeaders
  ): any;
  onBeforeUploadItem?(item: FileItem): any;
  onProgressItem?(item: FileItem, progress: number): any;
  onErrorItem?(
    item: FileItem,
    reposne: string,
    status: number,
    headers: ParsedResponseHeaders
  ): any;
  onWhenAddingFileFailed?(item: FileLikeObject, filter: any, options: any): any;
  onAfterAddingFile?(item: FileItem): any;
  onBuildItemForm?(item: FileItem, form: FormData): any;
}

@Injectable({
  providedIn: "root",
})
export class FileUploaderService {
  defaultOptions = {
    url: `${environment.api}/file/saveFile`,
    autoUpload: true,
    isHTML5: true,
    headers: [],
    maxFileSize: 4 * 1024 * 1024, //4MB
  };

  constructor(
    private amplifyService: AmplifyService,
    private sessionService: SessionService
  ) { }

  /**
   * Combina opções padrão com opções customizadas e injeta cabeçalhos dinâmicos.
   */
  private composeOptions(
    options?: FileUploaderOptions,
    jwt?: string
  ): FileUploaderOptions {
    const mergedOptions = {
      ...this.defaultOptions,
      ...(options || {}),
      headers: [...(options?.headers || [])],
    };

    if (jwt) {
      mergedOptions.headers.unshift({
        name: "Authorization",
        value: `Bearer ${jwt}`,
      });
    }

    const oemDealerSelected = this.sessionService.getOemDealerSelected();
    if (oemDealerSelected && oemDealerSelected.id) {
      mergedOptions.headers.push({
        name: "X-OemDealerId",
        value: oemDealerSelected.id,
      });
    }

    return mergedOptions;
  }

  /**
   * Obtém opções já com token atualizado. Caso falhe, devolve sem Authorization.
   */
  private async resolveOptions(
    options?: FileUploaderOptions
  ): Promise<FileUploaderOptions> {
    try {
      const jwt = await this.amplifyService.getJwt().toPromise();
      return this.composeOptions(options, jwt || undefined);
    } catch (err) {
      return this.composeOptions(options);
    }
  }

  /**
   * Adiciona gancho para atualizar token imediatamente antes de cada upload.
   */
  private attachTokenRefresh(
    uploader: FileUploader,
    options?: FileUploaderOptions
  ): void {
    const originalUploadItem = uploader.uploadItem.bind(uploader);
    uploader.uploadItem = (item: FileItem) => {
      this.resolveOptions(options)
        .then((freshOptions) => {
          uploader.setOptions(freshOptions);
        })
        .finally(() => originalUploadItem(item));
    };
  }

  /**
   * Aplica callbacks configurados externamente.
   */
  private applyActions(
    uploader: FileUploader,
    actions: FileUploaderActions | null
  ): void {
    if (actions === null) {
      return;
    }

    if (actions.onBeforeUploadItem) {
      uploader.onBeforeUploadItem = actions.onBeforeUploadItem;
    }

    if (actions.onBuildItemForm) {
      uploader.onBuildItemForm = actions.onBuildItemForm;
    }

    if (actions.onProgressItem) {
      uploader.onProgressItem = actions.onProgressItem;
    }

    if (actions.onWhenAddingFileFailed) {
      uploader.onWhenAddingFileFailed = actions.onWhenAddingFileFailed;
    }

    if (actions.onAfterAddingFile) {
      uploader.onAfterAddingFile = actions.onAfterAddingFile;
    }

    if (actions.onCompleteItem) {
      uploader.onCompleteItem = (
        item: FileItem,
        response: string,
        status: number,
        headers: ParsedResponseHeaders
      ) => {
        try {
          actions.onCompleteItem(
            item,
            JSON.parse(response),
            status,
            headers
          );
        } catch (err) {
          actions.onCompleteItem(item, response, status, headers);
        }
      };
    }

    if (actions.onCancelItem) {
      uploader.onCancelItem = actions.onCancelItem;
    }
  }

  /**
   * Retorna uma nova instancia de FileUploader
   */
  getInstance(
    actions: FileUploaderActions,
    options?: FileUploaderOptions
  ): Observable<FileUploader> {
    return new Observable((observer: Subscriber<FileUploader>) => {

      this.resolveOptions(options)
        .then((resolvedOptions) => {

          const fileUploader = new FileUploader(resolvedOptions);

          this.attachTokenRefresh(fileUploader, options);
          this.applyActions(fileUploader, actions);

          observer.next(fileUploader);
          observer.complete();
        });
    }).pipe(shareReplay(1));
  }
}
